import { FiHome, FiGrid, FiShoppingBag, FiUsers, FiUser } from "react-icons/fi";
import type { IconType } from "react-icons";
import { cn } from "./cn";
import { useI18n } from "../i18n";
import { useHashRoute, viewFromRoute, creatorFromRoute } from "./routing";

/**
 * Macro #31 — persistent mobile bottom navigation (<md only).
 *
 * Five canonical destinations: Home · Grid · Market · Social · Profile.
 * Desktop navigation (TopNavigation) is left completely intact; this bar is
 * `md:hidden`. Active tab is derived from the canonical hash route, never a
 * local flag. Touch targets are >= 44px and the bar is safe-area aware.
 */
type Tab = {
  key: string;
  testid: string;
  Icon: IconType;
  label: string;
  /** Navigate to this hash (without the leading '#'). */
  hash: string;
  /** Whether this tab is active for the current route. */
  isActive: (route: string) => boolean;
};

function go(hash: string) {
  // Canonical hash navigation (mirrors TopNavigation route items).
  if (hash === "") {
    if (window.location.hash && window.location.hash !== "#") {
      window.location.hash = "";
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
    return;
  }
  window.location.hash = hash;
}

export function MobileBottomNav() {
  const { t } = useI18n();
  const route = useHashRoute();
  const view = viewFromRoute(route);

  const tabs: Tab[] = [
    {
      key: "home",
      testid: "mobile-nav-home",
      Icon: FiHome,
      label: t("nav.overview"),
      hash: "",
      isActive: (r) => r === "" || r === "hero",
    },
    {
      key: "grid",
      testid: "mobile-nav-grid",
      Icon: FiGrid,
      label: "Grid",
      hash: "grid",
      isActive: (r) => r === "grid" || r === "purchase",
    },
    {
      key: "market",
      testid: "mobile-nav-market",
      Icon: FiShoppingBag,
      label: t("nav.marketplace"),
      hash: "marketplace",
      isActive: () => view === "marketplace",
    },
    {
      key: "social",
      testid: "mobile-nav-social",
      Icon: FiUsers,
      label: t("nav.feed"),
      hash: "feed",
      isActive: (r) => view === "feed" || view === "creator_profile" || !!creatorFromRoute(r),
    },
    {
      key: "profile",
      testid: "mobile-nav-profile",
      Icon: FiUser,
      label: t("nav.profile"),
      hash: "profile",
      isActive: () => view === "profile",
    },
  ];

  return (
    <nav
      data-testid="mobile-bottom-nav"
      aria-label="Primary mobile navigation"
      className={cn(
        "md:hidden fixed inset-x-0 bottom-0 z-topbar",
        "border-t border-border-divider",
        "bg-obsidian/92 backdrop-blur-xl",
      )}
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
    >
      <ul className="flex items-stretch justify-around">
        {tabs.map((tab) => {
          const active = tab.isActive(route);
          const { Icon } = tab;
          return (
            <li key={tab.key} className="flex-1">
              <button
                type="button"
                data-testid={tab.testid}
                aria-current={active ? "page" : undefined}
                onClick={() => go(tab.hash)}
                className={cn(
                  "flex h-14 min-h-[44px] w-full flex-col items-center justify-center gap-0.5",
                  "px-1 text-[10px] font-display uppercase tracking-[0.12em]",
                  "transition-colors duration-150 select-none",
                  active
                    ? "text-neon-teal"
                    : "text-text-secondary hover:text-text-primary",
                )}
              >
                <Icon
                  aria-hidden
                  className={cn("h-5 w-5 shrink-0", active && "drop-shadow-[0_0_6px_rgba(55,224,200,0.75)]")}
                />
                <span className="leading-none truncate max-w-full">{tab.label}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
